import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import type { ConsentPrompt } from '@/services/apis/oidcAPI';

/**
 * MCP 앱 연결 동의 (#216)
 *
 * 동적으로 등록한 앱(Claude · ChatGPT 등)은 누구나 등록할 수 있다. 그래서 코드를 내주기 전에
 * 어떤 앱이 어느 제품에 접근하려는지 사용자에게 묻는다. 앱 이름은 앱이 스스로 등록한 값이라
 * "확인되지 않은 앱" 으로 표시하고, 인증 후 이동할 주소(호스트)를 함께 보여 준다.
 *
 * OAuth 흐름으로만 들어온다. 메뉴 · 링크는 두지 않는다(사용자 지시 2026-10-18).
 */
interface Props {
  prompt: ConsentPrompt;
  onDecide: (decision: 'approve' | 'deny') => Promise<void>;
}

/** 화면에 보일 이름. 모르는 claim 은 이름 그대로 보인다(숨기지 않는다) */
const CLAIM_LABELS: Record<string, string> = {
  sub: 'GensApps 계정 식별자',
  email: '이메일',
  email_verified: '이메일 인증 여부',
  name: '이름',
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-gray-100 text-sm">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className="text-gray-900 text-right break-all">{children}</span>
  </div>
);

const OidcConsentCard: React.FC<Props> = ({ prompt, onDecide }) => {
  const [pending, setPending] = useState<'approve' | 'deny' | null>(null);
  const appName = prompt.client.name || '이름을 밝히지 않은 앱';

  const decide = (decision: 'approve' | 'deny') => {
    if (pending) return;
    setPending(decision);
    onDecide(decision).catch(() => setPending(null));
  };

  return (
    <div className="text-left">
      <h1 className="text-lg font-semibold text-gray-900 mb-1">앱 연결 요청</h1>
      <p className="text-sm text-gray-600 mb-4">
        아래 앱이 내 GensApps 계정으로 {prompt.resource.product}에 접근하려고 합니다.
      </p>
      <div className="mb-4">
        <Row label="앱">
          {appName}{' '}
          {!prompt.client.verified && <Badge variant="warning" size="sm">확인되지 않은 앱</Badge>}
        </Row>
        <Row label="서비스">{prompt.resource.product}</Row>
        <Row label="인증 후 이동할 주소">{prompt.client.redirect_host}</Row>
        <Row label="전달되는 정보">
          {prompt.shared_claims.map((claim) => CLAIM_LABELS[claim] ?? claim).join(', ')}
        </Row>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        허용하면 이 앱이 {prompt.resource.product}에서 내 계정으로 작업할 수 있습니다.
        직접 연결한 앱이 아니면 거부해 주세요.
      </p>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" disabled={pending !== null} onClick={() => decide('deny')}>
          {pending === 'deny' && (
            <span className="w-4 h-4 mr-2 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
          )}
          {pending === 'deny' ? '처리 중' : '거부'}
        </Button>
        <Button disabled={pending !== null} onClick={() => decide('approve')}>
          {pending === 'approve' && (
            <span className="w-4 h-4 mr-2 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          )}
          {pending === 'approve' ? '처리 중' : '허용'}
        </Button>
      </div>
    </div>
  );
};

export default OidcConsentCard;
